import { useMutation } from '@tanstack/react-query';
import { message } from 'antd';
import imageCompression from 'browser-image-compression';

import type { Picture } from 'app/typings';
import { apiMutation } from 'app/utils/request/api';
import { forgeUrl } from 'app/utils/request/forgeUrl';

const editPicturePath = 'pictures/:pictureId' as const;
const removePicturePath = 'hotels/:hotelId/pictures/:pictureId' as const;
const uploadPicturePath = 'pictures' as const;

const COMPRESSION_OPTIONS = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
} as const;

export function useEditPicture() {
  return useMutation({
    mutationFn: async (picture: Picture) =>
      apiMutation(
        'PATCH',
        {
          path: editPicturePath,
          params: { pictureId: picture.id.toString() },
        },
        picture
      ) as Promise<Picture>,
    onSuccess: () => {
      message.success('Picture successfully updated!');
    },
    onError: () => {
      message.error('Error editing picture');
    },
  });
}

export function useRemoveHotelPicture() {
  return useMutation({
    mutationFn: async ({
      hotelId,
      pictureId,
    }: {
      hotelId: number;
      pictureId: number;
    }) =>
      apiMutation('DELETE', {
        path: removePicturePath,
        params: {
          hotelId: hotelId.toString(),
          pictureId: pictureId.toString(),
        },
      }) as Promise<void>,
    onSuccess: () => {
      message.success('Picture successfully removed!');
    },
    onError: () => {
      message.error('Error removing picture from hotel gallery');
    },
  });
}

type UploadPictureParams = {
  file: File;
  existingPictures?: Picture[];
  onProgress?: (progress: number) => void;
};

export function useUploadPicture({ apiUrl }: { apiUrl: string }) {
  return useMutation({
    mutationFn: async ({
      file,

      existingPictures = [],
      onProgress,
    }: UploadPictureParams) => {
      const compressedFile = await imageCompression(file, COMPRESSION_OPTIONS);

      const formData = new FormData();

      formData.append('file', compressedFile);

      const url = forgeUrl(apiUrl, { path: uploadPicturePath });

      const picture = await new Promise<Picture>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.open('POST', url);

        if (onProgress) {
          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              onProgress(event.loaded / event.total);
            }
          };
        }

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch {
              reject(new Error('Invalid response format'));
            }
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error('Network error'));

        xhr.send(formData);
      });

      const isDuplicate = existingPictures.some(
        (pic) => pic.pictureId === picture.pictureId
      );

      if (isDuplicate) {
        throw new Error(`File ${file.name} already exists in the gallery`);
      }

      return picture;
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to upload picture');
    },
  });
}
